import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getTemplateById } from "../api/templates";
import CreateTemplate from "./CreateTemplate";
import ConfigureDocument from "./ConfigureDocument";
import useQueryParams from "../hooks/useQueryParams";
import { createSignRequest } from "../api/signRequests";
import { useLoading } from "../contexts/LoadingContext";
import { useAuth } from "../contexts/AuthContext";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import DocumentSigningConfirmationModal from "../components/DocumentSigningConfirmationModal";
import { hasDocumentTypeAccess } from "../utils/permissions";
const UseTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { user, usage, permissions } = useAuth();
  const queryParams = useQueryParams();

  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [finalData, setFinalData] = useState(null);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signDocumentLink, setSignDocumentLink] = useState(null);

  const { showLoader, hideLoader } = useLoading();
  // console.log("user", user);
  // console.log("templateData", templateData);

  useEffect(() => {
    // Check if user has exceeded sign requests limit
    if (usage?.exceededSignRequests) {
      toast.error(
        "You have reached the sign request limit for your plan. Please upgrade to send more sign requests.",
      );
      navigate("/dashboard/templates");
      return;
    }

    if (templateId) {
      const fetchTemplate = async () => {
        try {
          setIsLoading(true);
          const response = await getTemplateById(templateId);
          const template = response.data;

          // Check document type access
          if (permissions && permissions.templates) {
            const documentType = template.documentType || "";
            if (!hasDocumentTypeAccess(permissions, documentType)) {
              toast.error(
                "You do not have access to this document type template.",
              );
              navigate("/dashboard/templates");
              return;
            }
          }

          setTemplateData(template);
        } catch (error) {
          console.error("Error fetching template:", error);
          toast.error(error.message || "Failed to fetch template");
          // navigate('/dashboard/templates');
        } finally {
          setIsLoading(false);
        }
      };

      fetchTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const sendEmail = async (data) => {
    try {
      // Check if user has exceeded sign requests limit
      if (usage?.exceededSignRequests) {
        toast.error(
          "You have reached the sign request limit for your plan. Please upgrade to send more sign requests.",
        );
        return;
      }

      showLoader();
      const response = await createSignRequest(data);
      console.log("response", response);
      if (response.signDocumentLink) {
        setSignDocumentLink(response.signDocumentLink);
        setShowSigningModal(true);
      } else {
        toast.success("Email sent successfully");
        navigate("/dashboard/documents");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Failed to send email");
    } finally {
      hideLoader();
    }
  };

  const handleSignConfirm = () => {
    if (signDocumentLink) {
      window.open(signDocumentLink, "_blank");
    }
    setShowSigningModal(false);
    navigate("/dashboard/documents");
  };

  const handleSignCancel = () => {
    setShowSigningModal(false);
    navigate("/dashboard/documents");
  };

  return (
    <PagePermissionGuard
      module="templates"
      action="use_template"
      pageName="Use Template"
    >
      {!queryParams.step && templateData && (
        <CreateTemplate
          isFromUseTemplate={true}
          templateUseData={templateData}
          setFinalData={(data) => {
            setTemplateData((prev) => ({ ...prev, ...data }));
            setFinalData(data);
            const params = new URLSearchParams(location.search);
            params.set("step", "isFromUseTemplate");

            navigate({
              pathname: location.pathname,
              search: params.toString(),
            });
          }}
        />
      )}
      <div className="flex flex-1 min-h-0 flex-col h-full w-full overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64 shrink-0">
            <div className="relative mx-auto mb-6">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-primary rounded-full animate-spin mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-primary rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
            {queryParams.step === "isFromUseTemplate" && templateData && (
              <ConfigureDocument
                templateUseData={templateData}
                isFromUseTemplate={true}
                sendEmail={sendEmail}
              />
            )}
          </div>
        )}

        {/* Document Signing Confirmation Modal */}
        <DocumentSigningConfirmationModal
          isOpen={showSigningModal}
          onClose={handleSignCancel}
          onConfirm={handleSignConfirm}
          title="Document Signing Required"
          message="This document requires your signature. Would you like to sign it now?"
        />
      </div>
    </PagePermissionGuard>
  );
};

export default UseTemplate;
